import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Gem, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface MediumshipSession {
  id: string;
  session_date: string;
  created_at: string;
  spirit_name: string;
  recipient_name: string;
  evidential_info: { type: string; detail: string }[];
  validations: { detail: string; validated: boolean }[];
  session_notes: string;
  accuracy_notes: string;
  development_insights: string;
}

export default function MediumshipJournal() {
  const [sessions, setSessions] = useState<MediumshipSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    spirit_name: '',
    recipient_name: '',
    evidential_info: [{ type: '', detail: '' }],
    session_notes: '',
    accuracy_notes: '',
    development_insights: ''
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('mediumship_sessions')
      .select('*')
      .eq('user_id', user.id)
      .order('session_date', { ascending: false });

    if (error) {
      toast({ title: 'Error loading sessions', variant: 'destructive' });
    } else {
      setSessions((data as any) || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('mediumship_sessions')
      .insert({
        user_id: user.id,
        session_date: new Date().toISOString().split('T')[0],
        ...formData
      });

    if (error) {
      toast({ title: 'Error saving session', variant: 'destructive' });
    } else {
      toast({ title: 'Mediumship session saved' });
      setFormData({
        spirit_name: '',
        recipient_name: '',
        evidential_info: [{ type: '', detail: '' }],
        session_notes: '',
        accuracy_notes: '',
        development_insights: ''
      });
      setShowForm(false);
      loadSessions();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading mediumship journal...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gem className="h-5 w-5 text-primary animate-pulse" />
            Mediumship Practice Journal
          </CardTitle>
          <CardDescription>
            Document spirit communication sessions with evidential details. Record validations to build confidence in your mediumship abilities and track development over time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowForm(!showForm)} className="mb-4">
            <Plus className="h-4 w-4 mr-2" />
            Log Session
          </Button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 border border-border rounded-lg bg-card animate-fade-in">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Spirit Communicating</Label>
                  <Input
                    value={formData.spirit_name}
                    onChange={(e) => setFormData({ ...formData, spirit_name: e.target.value })}
                    placeholder="Name if known"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Recipient (optional)</Label>
                  <Input
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                    placeholder="For whom was reading?"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Evidential Information</Label>
                <Textarea
                  placeholder="Names, dates, physical descriptions, mannerisms, objects, locations, causes of death, personality traits, memories shared..."
                  rows={4}
                  value={formData.evidential_info[0]?.detail || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    evidential_info: [{ type: 'evidence', detail: e.target.value }] 
                  })}
                />
                <p className="text-xs text-muted-foreground">
                  Specific details that can be verified and wouldn't be guessed
                </p>
              </div>

              <div className="space-y-2">
                <Label>Session Notes</Label>
                <Textarea
                  value={formData.session_notes}
                  onChange={(e) => setFormData({ ...formData, session_notes: e.target.value })}
                  placeholder="Messages delivered, emotional tone, how the connection felt..."
                  rows={4}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Accuracy & Validation Notes</Label>
                <Textarea
                  value={formData.accuracy_notes}
                  onChange={(e) => setFormData({ ...formData, accuracy_notes: e.target.value })}
                  placeholder="What was validated? What resonated with the recipient? What was unclear?"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Development Insights</Label>
                <Textarea
                  value={formData.development_insights}
                  onChange={(e) => setFormData({ ...formData, development_insights: e.target.value })}
                  placeholder="What did you learn? Areas to improve? Breakthroughs?"
                  rows={2}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Save Session</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {sessions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Start logging mediumship sessions to track evidential hits and development.
              </p>
            ) : (
              sessions.map((session) => (
                <Card key={session.id} className="bg-muted/30 animate-fade-in">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <CardTitle className="text-lg">
                          {session.spirit_name || 'Unknown Spirit'}
                          {session.recipient_name && (
                            <span className="text-sm font-normal text-muted-foreground"> → {session.recipient_name}</span>
                          )}
                        </CardTitle>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(session.session_date), 'PPP')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-light text-primary mb-1">Session Notes:</p>
                      <p className="text-sm text-muted-foreground font-light">{session.session_notes}</p>
                    </div>
                    {session.accuracy_notes && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-sm font-light text-primary mb-1 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Validation:
                        </p>
                        <p className="text-sm text-muted-foreground font-light">{session.accuracy_notes}</p>
                      </div>
                    )}
                    {session.development_insights && (
                      <div>
                        <p className="text-sm font-light text-primary mb-1">Insights:</p>
                        <p className="text-sm text-muted-foreground font-light italic">{session.development_insights}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
            <h4 className="font-light text-primary mb-2">🕊️ Evidential Mediumship Guidelines</h4>
            <ul className="text-sm space-y-1 text-muted-foreground font-light">
              <li>• Focus on specific, verifiable details, not generalizations</li>
              <li>• Note physical descriptions, names, dates, locations</li>
              <li>• Record personality traits and mannerisms</li>
              <li>• Document how information was received (seen, felt, heard, known)</li>
              <li>• Always ask for validation from recipients when possible</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
